import axios, { AxiosError } from "axios";
import CryptoJS from "crypto-js";

import { t } from "i18next";
import { keycloakService } from "services/keycloakService";

type PortalApiParams<TVariables, TBody> = {
  path: string;
  body?: TBody;
  query?: string;
  variables?: TVariables;
  token: string | undefined;
};

const levPortalUrl = process.env.REACT_APP_API_URL_LEV;
const adeclaBackUrl = process.env.REACT_APP_API_ADECLA_BACK;
const secretKey = process.env.REACT_APP_ENCRYPT_KEY;

export const portalAPI = async <
  TData,
  TVariables = Record<string, never>,
  TBody = Record<string, Record<string, string> | unknown>
>({
  path,
  query,
  variables,
  body = {} as TBody,
  token,
}: PortalApiParams<TVariables, TBody>) => {
  if (!token) {
    throw new Error("No API token");
  }

  if (!levPortalUrl) {
    throw new Error("No API URL defined");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const response = await axios.post<TData>(
    `${levPortalUrl}/${path}`,
    { query, variables, ...body },
    { headers }
  );

  if (response.status >= 300) {
    throw new AxiosError(t("messages.error"));
  }

  return response.data;
};

export const encryptHeader = (value: string) => {
  if (!secretKey) {
    throw new Error("Encryption key is not set in environment variables.");
  }
  const key = CryptoJS.SHA256(secretKey);
  const encrypted = CryptoJS.AES.encrypt(value, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.toString();
};

export const adeclaBackAPI = async <TData, TBody = Record<string, unknown>>({
  path,
  method = "GET",
  body,
  activeContactId,
}: {
  path: string;
  method?: "GET" | "POST";
  body?: TBody;
  activeContactId?: string | number;
}) => {
  const token = await keycloakService.getToken();

  if (!token) {
    throw new Error("No API token");
  }

  const url = `${adeclaBackUrl}/api`;

  const headers: Record<string, string> = {
    "x-fa-token": token,
    "Content-Type": "application/json",
  };
  if (activeContactId !== undefined && activeContactId !== "") {
    headers["x-fa-active-contact"] = String(activeContactId);
  }

  if (method === "GET") {
    const response = await axios.get<TData>(`${url}/${path}`, { headers });
    if (response.status >= 400) {
      throw new AxiosError(t("messages.error"));
    }
    return response.data;
  }

  // POST method
  const response = await axios.post<TData>(`${url}/${path}`, body, { headers });
  if (response.status >= 400) {
    throw new AxiosError(t("messages.error"));
  }
  return response.data;
};
